import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import hrAnalyticsService from "../../../api/services/hrAnalyticsService";
import { useCustomAlert } from "../../../hooks/useCustomAlert";
import {
  BarChart3,
  TrendingUp,
  Users,
  Shield,
  Download,
  RefreshCw,
  PieChart,
  IndianRupee,
  Circle,
  ChevronDown,
  CalendarRange,
  BookOpen,
  Award,
  CalendarCheck,
  FileText,
  Clock,
  ClipboardList,
  GraduationCap
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import { formatIndianAmount, formatIndianNumber } from "../../../utils/amountFormatter";

// CSV Export Function
const exportToCSV = (dashboardKPIs, attritionTrends, recruitmentFunnel, salaryTrends, comprehensiveStats, formatCurrency, formatInLakhs) => {
  const rows = [];

  // Add header row
  rows.push([
    'HR Analytics & Compliance Dashboard Report',
    `Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
  ]);
  rows.push([]); // Empty row

  // Key Performance Indicators Section
  rows.push(['Key Performance Indicators']);
  rows.push(['Metric', 'Value']);
  rows.push(['Total Staff', formatIndianNumber(dashboardKPIs?.totalEmployees?.value || 0)]);
  rows.push(['Attrition Rate', `${dashboardKPIs?.attritionRate?.value || 0}%`]);
  rows.push(['Average Salary', dashboardKPIs?.avgSalary?.value > 0 ? formatInLakhs(dashboardKPIs.avgSalary.value) : 'N/A']);
  rows.push([]); // Empty row

  // Attrition Trends Section
  if (attritionTrends && attritionTrends.length > 0) {
    rows.push(['Attrition Trends']);
    rows.push(['Month', 'Voluntary', 'Involuntary', 'Total']);

    attritionTrends.forEach((item) => {
      rows.push([
        item.month || '',
        item.voluntary || 0,
        item.involuntary || 0,
        item.total || 0
      ]);
    });
    rows.push([]); // Empty row
  }

  // Recruitment Funnel Section
  if (recruitmentFunnel) {
    rows.push(['Recruitment Funnel']);
    rows.push(['Stage', 'Count']);

    const statusBreakdown = recruitmentFunnel.statusBreakdown || {};
    const applied = statusBreakdown.applied || 0;
    const screening = statusBreakdown.screening || 0;
    const shortlisted = statusBreakdown.shortlisted || 0;
    const interview = statusBreakdown.interview || 0;
    const interviewed = statusBreakdown.interviewed || 0;
    const offer = statusBreakdown.offer || 0;
    const hired = statusBreakdown.hired || 0;
    const rejected = statusBreakdown.rejected || 0;
    const total = applied + screening + shortlisted + interview + interviewed + offer + hired + rejected;

    rows.push(['Total Applicants', formatIndianNumber(total)]);
    rows.push(['Applied', formatIndianNumber(applied)]);
    rows.push(['Screening', formatIndianNumber(screening)]);
    rows.push(['Shortlisted', formatIndianNumber(shortlisted)]);
    rows.push(['Interview Scheduled', formatIndianNumber(interview)]);
    rows.push(['Interviewed', formatIndianNumber(interviewed)]);
    rows.push(['Offer', formatIndianNumber(offer)]);
    rows.push(['Hired', formatIndianNumber(hired)]);
    rows.push(['Rejected', formatIndianNumber(rejected)]);
    rows.push(['Conversion Rate', `${(recruitmentFunnel.conversionRate || 0).toFixed(1)}%`]);
    rows.push([]); // Empty row
  }

  // Salary Trends Section
  if (salaryTrends && salaryTrends.length > 0) {
    rows.push(['Salary Trends']);
    rows.push(['Department', 'Average Salary']);

    salaryTrends.forEach((dept) => {
      rows.push([
        dept.department || '',
        formatCurrency(dept.avgSalary)
      ]);
    });
    rows.push([]); // Empty row
  }

  // Department Breakdown Section
  if (comprehensiveStats?.departmentBreakdown && comprehensiveStats.departmentBreakdown.length > 0) {
    rows.push(['Department Breakdown']);
    rows.push(['Department', 'Active Employees']);

    comprehensiveStats.departmentBreakdown.slice(0, 5).forEach((dept) => {
      rows.push([
        dept._id || 'Unknown',
        dept.activeEmployees || 0
      ]);
    });
    rows.push([]); // Empty row
  }

  // Gender Distribution Section
  if (comprehensiveStats?.genderDistribution && comprehensiveStats.genderDistribution.length > 0) {
    rows.push(['Gender Distribution']);
    rows.push(['Gender', 'Count']);

    comprehensiveStats.genderDistribution.forEach((gender) => {
      rows.push([
        gender._id || 'Unknown',
        gender.count || 0
      ]);
    });
    rows.push([]); // Empty row
  }

  // Employment Type Distribution Section
  if (comprehensiveStats?.employmentTypeDistribution && comprehensiveStats.employmentTypeDistribution.length > 0) {
    rows.push(['Employment Type Distribution']);
    rows.push(['Employment Type', 'Count']);

    comprehensiveStats.employmentTypeDistribution.forEach((type) => {
      rows.push([
        (type._id || 'Unknown').replace('-', ' '),
        type.count || 0
      ]);
    });
  }

  // Convert rows to CSV format
  const csvContent = rows.map(row => {
    return row.map(cell => {
      // Escape quotes and wrap in quotes if contains comma, newline, or quote
      const cellValue = String(cell || '');
      if (cellValue.includes(',') || cellValue.includes('\n') || cellValue.includes('"')) {
        return `"${cellValue.replace(/"/g, '""')}"`;
      }
      return cellValue;
    }).join(',');
  }).join('\n');

  // Create and download CSV file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `HR_Analytics_Dashboard_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const HRAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showError } = useCustomAlert();
  const [selectedPeriod, setSelectedPeriod] = useState("current-month");
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });


  // React Query: Fetch dashboard summary
  const {
    data: analyticsDataResponse,
    isLoading: loadingDashboard,
  } = useQuery({
    queryKey: ["hrDashboardSummary", selectedPeriod, dateRange],
    queryFn: async () => {
      const response = await hrAnalyticsService.getDashboardSummary(
        selectedPeriod,
        "all",
        dateRange.startDate,
        dateRange.endDate
      );
      if (response.responseCode === 200) {
        return response.data;
      }
      throw new Error(response.responseMessage || "Failed to load analytics data");
    },
    staleTime: 0,
  });

  const analyticsData = analyticsDataResponse || {
    totalEmployees: 0,
    activeEmployees: 0,
    activeRecruitments: 0,
    pendingLeaves: 0,
    trainingPrograms: 0,
    newHiresThisMonth: 0,
    attritionRate: 0,
    averageTenure: 0,
    departmentDistribution: {},
    recentHires: [],
    leaveUtilization: {
      totalLeaves: 0,
      usedLeaves: 0,
      utilizationRate: 0,
    },
    performanceMetrics: {
      reviewsCompleted: 0,
      reviewsPending: 0,
      averageRating: 0,
    },
    trainingMetrics: {
      programsCompleted: 0,
      programsInProgress: 0,
      completionRate: 0,
    },
  };

  // React Query: Fetch attrition trends
  // Show 12 months for "year" or "all-data", otherwise 6 months
  const monthsToShow = selectedPeriod === "year" || selectedPeriod === "all-data" ? 12 : 6;
  const {
    data: attritionTrendsResponse,
    isLoading: loadingAttrition,
  } = useQuery({
    queryKey: ["attritionTrends", selectedPeriod, monthsToShow, dateRange],
    queryFn: async () => {
      const response = await hrAnalyticsService.getAttritionTrends(
        monthsToShow,
        selectedPeriod,
        dateRange.startDate,
        dateRange.endDate
      );
      return response.trends || [];
    },
    staleTime: 0,
  });

  const attritionTrends = attritionTrendsResponse || [];

  // React Query: Fetch recruitment funnel
  const {
    data: recruitmentFunnelResponse,
    isLoading: loadingFunnel,
  } = useQuery({
    queryKey: ["recruitmentFunnel", selectedPeriod, dateRange],
    queryFn: async () => {
      return await hrAnalyticsService.getRecruitmentFunnel(
        selectedPeriod,
        "all",
        dateRange.startDate,
        dateRange.endDate
      );
    },
    staleTime: 0,
  });

  const recruitmentFunnel = recruitmentFunnelResponse || null;

  // React Query: Fetch salary trends
  const {
    data: salaryTrendsResponse,
    isLoading: loadingSalary,
  } = useQuery({
    queryKey: ["salaryTrends", selectedPeriod, dateRange],
    queryFn: async () => {
      const response = await hrAnalyticsService.getSalaryTrends(
        selectedPeriod,
        "all",
        dateRange.startDate,
        dateRange.endDate
      );
      if (response.responseCode === 200 && response.data) {
        return response.data;
      } else if (Array.isArray(response)) {
        return response;
      }
      return [];
    },
    staleTime: 0,
  });

  const salaryTrends = salaryTrendsResponse || [];

  // React Query: Fetch comprehensive HR stats
  const {
    data: comprehensiveStatsResponse,
    isLoading: loadingComprehensive,
  } = useQuery({
    queryKey: ["comprehensiveHRStats", selectedPeriod, dateRange],
    queryFn: async () => {
      return await hrAnalyticsService.getComprehensiveHRStats(
        selectedPeriod,
        "all",
        dateRange.startDate,
        dateRange.endDate
      );
    },
    staleTime: 0,
  });

  const comprehensiveStats = comprehensiveStatsResponse || null;

  // Combined loading state
  // Only show full-page loading if the primary dashboard data is missing
  const loading = loadingDashboard;

  // Format dashboard KPIs with real data from comprehensive stats
  const dashboardKPIs = {
    totalEmployees: {
      value: comprehensiveStats?.overview?.totalEmployees ?? analyticsData.totalEmployees,
      change: analyticsData.periodMetrics?.growthMetrics?.totalEmployeesGrowth || 0,
      trend: (analyticsData.periodMetrics?.growthMetrics?.totalEmployeesGrowth || 0) >= 0 ? "up" : "down",
    },
    attritionRate: {
      value: analyticsData.attritionRate || 0,
      change: analyticsData.periodMetrics?.growthMetrics?.attritionGrowth || 0,
      trend: (analyticsData.periodMetrics?.growthMetrics?.attritionGrowth || 0) >= 0 ? "up" : "down",
    },
    activeStaff: {
      value: comprehensiveStats?.overview?.activeEmployees ?? analyticsData.activeEmployees,
    },
    inactiveStaff: {
      value: comprehensiveStats?.overview?.inactiveEmployees ?? analyticsData.inactiveEmployees,
    },
    exitStaff: {
      value: (comprehensiveStats?.overview?.terminatedEmployees || 0) + (comprehensiveStats?.overview?.resignedEmployees || 0) || analyticsData.exitedEmployees || 0,
    },
    // Now using real salary data from comprehensive stats
    avgSalary: {
      value: (() => {
        // First try to get the average salary from salaryAnalysis
        if (comprehensiveStats?.salaryAnalysis?.avgSalary) {
          return comprehensiveStats.salaryAnalysis.avgSalary;
        }

        // Fallback to calculating from department breakdown
        if (comprehensiveStats?.departmentBreakdown?.length > 0) {
          const calculatedAvg =
            comprehensiveStats.departmentBreakdown.reduce(
              (sum, dept) => sum + (dept.avgSalary || 0),
              0
            ) / comprehensiveStats.departmentBreakdown.length;
          return calculatedAvg;
        }
        return 0;
      })(),
      change:
        analyticsData.periodMetrics?.growthMetrics?.activeEmployeesGrowth || 0,
      trend:
        (analyticsData.periodMetrics?.growthMetrics?.activeEmployeesGrowth ||
          0) >= 0
          ? "up"
          : "down",
    },
    // Now using real compliance data from comprehensive stats
    complianceScore: {
      value: (() => {
        if (comprehensiveStats?.complianceStatus) {
          const totalDocs =
            comprehensiveStats.complianceStatus.totalEmployees * 5;
          const completedDocs =
            comprehensiveStats.complianceStatus.withPAN +
            comprehensiveStats.complianceStatus.withAadhaar +
            comprehensiveStats.complianceStatus.withUAN +
            comprehensiveStats.complianceStatus.withESI +
            comprehensiveStats.complianceStatus.withBankAccount;
          const score = Math.round((completedDocs / totalDocs) * 100);
          return score;
        } else if (comprehensiveStats?.overview?.totalEmployees > 0) {
          return 85;
        } else {
          return 0;
        }
      })(),
      change:
        analyticsData.periodMetrics?.growthMetrics?.activeEmployeesGrowth || 0,
      trend:
        (analyticsData.periodMetrics?.growthMetrics?.activeEmployeesGrowth ||
          0) >= 0
          ? "up"
          : "down",
    },

  };

  const formatCurrency = (amount) => {
    return formatIndianAmount(amount);
  };

  const formatInLakhs = (amount) => {
    return formatIndianAmount(amount);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "text-red-600 bg-red-50";
      case "medium":
        return "text-yellow-600 bg-yellow-50";
      case "low":
        return "text-green-600 bg-green-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  // Show loading state only for initial load of core analytics
  if (loading && !analyticsDataResponse) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center space-y-4">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
            <p className="text-gray-600">Loading HR Analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            HR Analytics & Compliance
          </h2>
          <p className="text-sm text-gray-600">
            Comprehensive HR insights and compliance monitoring
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            <select
              value={selectedPeriod}
              onChange={(e) => {
                const newPeriod = e.target.value;
                setSelectedPeriod(newPeriod);
                if (newPeriod !== 'custom') {
                  setDateRange({ startDate: '', endDate: '' });
                }
              }}
              className="appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
            >
              <option value="custom">Custom Range</option>
              <option value="7-days">Last 7 Days</option>
              <option value="current-month">Current Month</option>
              <option value="last-month">Last Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
              <option value="all-data">All Data</option>
            </select>
          </div>

          {selectedPeriod === 'custom' && (
            <div className="flex-shrink-0">
              <DateRangeFilter
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onChange={(range) => setDateRange(range)}
              />
            </div>
          )}

          {/* CSV Export */}
          <Button
            variant="outline"
            onClick={() => exportToCSV(
              dashboardKPIs,
              attritionTrends,
              recruitmentFunnel,
              salaryTrends,
              comprehensiveStats,
              formatCurrency,
              formatInLakhs
            )}
            className="flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-blue-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Staff</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatIndianNumber(dashboardKPIs.totalEmployees.value)}
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-full">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-emerald-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Staff</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatIndianNumber(dashboardKPIs.activeStaff.value)}
              </p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-full">
              <Users className="h-6 w-6 text-emerald-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-orange-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Inactive Staff</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatIndianNumber(dashboardKPIs.inactiveStaff.value)}
              </p>
            </div>
            <div className="p-3 bg-orange-50 rounded-full">
              <Users className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-rose-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Exit Staff</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatIndianNumber(dashboardKPIs.exitStaff.value)}
              </p>
            </div>
            <div className="p-3 bg-rose-50 rounded-full">
              <Users className="h-6 w-6 text-rose-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-red-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Attrition Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboardKPIs.attritionRate.value}%
              </p>
            </div>
            <div className="p-3 bg-red-50 rounded-full">
              <TrendingUp className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-green-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Monthly Avg. Salary</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboardKPIs.avgSalary.value > 0
                  ? formatIndianAmount(dashboardKPIs.avgSalary.value)
                  : "N/A"}
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-full">
              <IndianRupee className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-indigo-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Compliance score</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboardKPIs.complianceScore.value}%
              </p>
            </div>
            <div className="p-3 bg-indigo-50 rounded-full">
              <Shield className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Cards Row - Two columns per row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attrition Trends */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Attrition Trends
            </h3>
            <button className="text-gray-400 hover:text-gray-600">
              <BarChart3 className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-6">
            {loadingAttrition ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
              </div>
            ) : attritionTrends.length > 0 ? (
              attritionTrends.map((item, index) => (
                <div key={index} className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium text-gray-600">
                    {item.month}
                  </span>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">
                        Voluntary: {formatIndianNumber(item.voluntary)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">
                        Involuntary: {formatIndianNumber(item.involuntary)}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      Total: {formatIndianNumber(item.total)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-2">
                  <TrendingUp className="h-8 w-8 mx-auto" />
                </div>
                <p className="text-sm text-gray-500">
                  No attrition data available
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Data will appear when employees resign
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Recruitment Funnel */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Recruitment Funnel
            </h3>
            <button className="text-gray-400 hover:text-gray-600">
              <PieChart className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-3">
            {loadingFunnel ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
              </div>
            ) : recruitmentFunnel ? (
              <>
                {/* Total Applications - sum of all stages */}
                <div className="flex items-center justify-between pb-3 border-b-2 border-gray-200 mb-3">
                  <span className="text-sm font-medium text-gray-900">Total Applicants</span>
                  <span className="text-base font-bold text-blue-600">
                    {(() => {
                      const applied = recruitmentFunnel.statusBreakdown?.applied || 0;
                      const screening = recruitmentFunnel.statusBreakdown?.screening || 0;
                      const shortlisted = recruitmentFunnel.statusBreakdown?.shortlisted || 0;
                      const interview = recruitmentFunnel.statusBreakdown?.interview || 0;
                      const interviewed = recruitmentFunnel.statusBreakdown?.interviewed || 0;
                      const offer = recruitmentFunnel.statusBreakdown?.offer || 0;
                      const hired = recruitmentFunnel.statusBreakdown?.hired || 0;
                      const rejected = recruitmentFunnel.statusBreakdown?.rejected || 0;
                      const total = applied + screening + shortlisted + interview + interviewed + offer + hired + rejected;
                      return formatIndianNumber(total);
                    })()}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-gray-600">Applied</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatIndianNumber(recruitmentFunnel.statusBreakdown?.applied || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-gray-600">Screening</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatIndianNumber(recruitmentFunnel.statusBreakdown?.screening || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-gray-600">Shortlisted</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatIndianNumber(recruitmentFunnel.statusBreakdown?.shortlisted || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-gray-600">Interview Scheduled</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatIndianNumber(recruitmentFunnel.statusBreakdown?.interview || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-gray-600">Interviewed</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatIndianNumber(recruitmentFunnel.statusBreakdown?.interviewed || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-gray-600">Offer</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatIndianNumber(recruitmentFunnel.statusBreakdown?.offer || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-gray-600">Hired</span>
                    <span className="text-sm font-semibold text-green-600">
                      {formatIndianNumber(recruitmentFunnel.statusBreakdown?.hired || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-gray-600">Rejected</span>
                    <span className="text-sm font-semibold text-red-600">
                      {formatIndianNumber(recruitmentFunnel.statusBreakdown?.rejected || 0)}
                    </span>
                  </div>
                </div>
                <div className="border-t pt-3 mt-3">
                  <div className="flex items-center justify-between bg-green-50 rounded-md px-3 py-2">
                    <span className="text-sm font-medium text-gray-900">
                      Conversion Rate
                    </span>
                    <span className="text-base font-bold text-green-600">
                      {recruitmentFunnel.conversionRate?.toFixed(1) || 0}%
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-2">
                  <PieChart className="h-8 w-8 mx-auto" />
                </div>
                <p className="text-sm text-gray-500">
                  No recruitment data available
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Data will appear when candidates apply
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Comprehensive HR Statistics - 2 columns per row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Salary Trends by Department */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Salary Trends
            </h3>
            <button className="text-gray-400 hover:text-gray-600">
              <TrendingUp className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-2.5">
            {loadingSalary ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
              </div>
            ) : salaryTrends.length > 0 ? (
              salaryTrends.map((dept, index) => (
                <div key={index} className="flex items-start py-2 border-b border-gray-100 last:border-0 gap-4">
                  <span className="text-sm font-medium text-gray-700 break-words flex-[2] min-w-0">
                    {dept.department}
                  </span>
                  <span className="text-sm font-semibold text-gray-900 flex-shrink-0 text-right">
                    {formatCurrency(dept.avgSalary)}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-2">
                  <IndianRupee className="h-8 w-8 mx-auto" />
                </div>
                <p className="text-sm text-gray-500">
                  No salary data available
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Data will appear when employee salary information is available
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Department Breakdown */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Department Breakdown
            </h3>
            <button className="text-gray-400 hover:text-gray-600">
              <BarChart3 className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-2.5">
            {loadingComprehensive ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
              </div>
            ) : comprehensiveStats?.departmentBreakdown &&
              comprehensiveStats.departmentBreakdown.length > 0 ? (
              comprehensiveStats.departmentBreakdown
                .slice(0, 5)
                .map((dept, index) => (
                  <div
                    key={index}
                    className="flex items-start py-2 border-b border-gray-100 last:border-0 gap-4"
                  >
                    <span className="text-sm font-medium text-gray-700 break-words flex-[2] min-w-0">
                      {dept._id || "Unknown"}
                    </span>
                    <div className="flex items-center space-x-1.5 flex-shrink-0">
                      <Circle className="h-2 w-2 fill-green-500 text-green-500" />
                      <span className="text-sm font-semibold text-gray-900">
                        {dept.activeEmployees || 0}
                      </span>
                      <span className="text-xs text-gray-500">active</span>
                    </div>
                  </div>
                ))
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-2">
                  <BarChart3 className="h-8 w-8 mx-auto" />
                </div>
                <p className="text-sm text-gray-500">
                  No department data available
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Data will appear when employees are assigned to departments
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Gender Distribution */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Gender Distribution
            </h3>
            <button className="text-gray-400 hover:text-gray-600">
              <PieChart className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-2.5">
            {loadingComprehensive ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
              </div>
            ) : comprehensiveStats?.genderDistribution &&
              comprehensiveStats.genderDistribution.length > 0 ? (
              comprehensiveStats.genderDistribution.map((gender, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <span className="text-sm font-medium text-gray-700 capitalize">
                    {gender._id || "Unknown"}
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {gender.count || 0}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-2">
                  <PieChart className="h-8 w-8 mx-auto" />
                </div>
                <p className="text-sm text-gray-500">
                  No gender data available
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Data will appear when employee gender information is available
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Employment Type */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Employment Type
            </h3>
            <button className="text-gray-400 hover:text-gray-600">
              <Users className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-2.5">
            {loadingComprehensive ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
              </div>
            ) : comprehensiveStats?.employmentTypeDistribution &&
              comprehensiveStats.employmentTypeDistribution.length > 0 ? (
              comprehensiveStats.employmentTypeDistribution.map(
                (type, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                  >
                    <span className="text-sm font-medium text-gray-700 capitalize">
                      {type._id?.replace("-", " ") || "Unknown"}
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {type.count || 0}
                    </span>
                  </div>
                )
              )
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-2">
                  <Users className="h-8 w-8 mx-auto" />
                </div>
                <p className="text-sm text-gray-500">
                  No employment type data available
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Data will appear when employee employment type information is available
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Hires */}
      {analyticsData.recentHires && analyticsData.recentHires.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Recent Hires
            </h3>
            <CalendarCheck className="h-5 w-5 text-gray-400" />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Join Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {analyticsData.recentHires.map((hire, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{hire.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{hire.department}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{hire.joinDate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRAnalyticsDashboard;
